import { getParentSaleLines, getNewestParentLineId } from '@/components/pos/posCart'
import { canHoldSale } from '@/components/pos/posHold'

/** @typedef {import('@/types/api').Sale} Sale */

/**
 * @param {KeyboardEvent} event
 */
export function shouldIgnorePosShortcut(event) {
  const target = event.target
  if (!target || typeof target !== 'object' || !('tagName' in target)) {
    return false
  }

  const tag = String(target.tagName).toUpperCase()
  const isEditable = tag === 'INPUT'
    || tag === 'TEXTAREA'
    || tag === 'SELECT'
    || Boolean(target.isContentEditable)

  if (!isEditable) {
    return false
  }

  if (event.key === 'Escape') {
    return false
  }

  if (/^F\d{1,2}$/.test(event.key)) {
    return false
  }

  return true
}

/**
 * @param {string} key
 */
export function isPlusKey(key) {
  return key === '+' || key === '=' || key === 'NumpadAdd'
}

/**
 * @param {string} key
 */
export function isMinusKey(key) {
  return key === '-' || key === 'NumpadSubtract'
}

/**
 * @param {number | string | null | undefined} currentQuantity
 * @param {number} delta
 */
export function adjustLineQuantity(currentQuantity, delta) {
  const current = Math.max(1, Number(currentQuantity) || 1)
  if (delta < 0) {
    return Math.max(1, current + delta)
  }
  return current + delta
}

/**
 * @param {import('@/types/api').SaleLine[]} parentLines
 * @param {number | null | undefined} selectedLineId
 */
export function resolveActiveLineId(parentLines, selectedLineId) {
  if (!parentLines.length) {
    return null
  }

  if (selectedLineId && parentLines.some((line) => line.id === selectedLineId)) {
    return selectedLineId
  }

  return getNewestParentLineId(parentLines)
}

export function focusPosCustomerSearch() {
  document.getElementById('pos-customer-search')?.focus()
}

/**
 * @param {object} options
 * @param {Sale | null} options.sale
 * @param {boolean} options.mutating
 * @param {boolean} options.paymentOpen
 * @param {boolean} options.suspendedOpen
 * @param {boolean} options.appointmentOpen
 * @param {boolean} options.packageOpen
 * @param {boolean} options.redeemOpen
 * @param {boolean} options.discountOpen
 * @param {boolean} options.noteOpen
 * @param {boolean} options.customerHistoryOpen
 * @param {number | null} options.selectedLineId
 * @param {() => void} options.onFocusCustomer
 * @param {() => void} options.onOpenPayment
 * @param {() => void | Promise<void>} options.onHoldSale
 * @param {() => void | Promise<void>} options.onOpenSuspended
 * @param {() => void} options.onOpenDiscount
 * @param {() => void} options.onOpenVoucher
 * @param {() => void | Promise<void>} options.onClosePayment
 * @param {() => void} options.onCloseSuspended
 * @param {() => void} options.onCloseAppointment
 * @param {() => void} options.onClosePackage
 * @param {() => void} options.onCloseRedeem
 * @param {() => void} options.onCloseDiscount
 * @param {() => void} options.onCloseNote
 * @param {() => void} options.onCloseCustomerHistory
 * @param {() => void} [options.onClearLineSelection]
 * @param {() => void} options.onAppointmentShortcut
 * @param {() => void} options.onPackageShortcut
 * @param {() => void} options.onRedeemShortcut
 * @param {(lineId: number, quantity: number) => void | Promise<void>} options.onChangeLineQuantity
 */
export function handlePosShortcutKeyDown(event, options) {
  if (shouldIgnorePosShortcut(event)) {
    return false
  }

  const {
    sale,
    mutating,
    paymentOpen,
    suspendedOpen,
    appointmentOpen,
    packageOpen,
    redeemOpen,
    discountOpen,
    noteOpen,
    customerHistoryOpen,
    selectedLineId,
    onFocusCustomer,
    onOpenPayment,
    onHoldSale,
    onOpenSuspended,
    onOpenDiscount,
    onOpenVoucher,
    onClosePayment,
    onCloseSuspended,
    onCloseAppointment,
    onClosePackage,
    onCloseRedeem,
    onCloseDiscount,
    onCloseNote,
    onCloseCustomerHistory,
    onClearLineSelection,
    onAppointmentShortcut,
    onPackageShortcut,
    onRedeemShortcut,
    onChangeLineQuantity,
  } = options

  if (event.key === 'Escape') {
    if (paymentOpen && !mutating) {
      event.preventDefault()
      onClosePayment()
      return true
    }

    if (suspendedOpen && !mutating) {
      event.preventDefault()
      onCloseSuspended()
      return true
    }

    if (appointmentOpen && !mutating) {
      event.preventDefault()
      onCloseAppointment()
      return true
    }

    if (packageOpen && !mutating) {
      event.preventDefault()
      onClosePackage()
      return true
    }

    if (redeemOpen && !mutating) {
      event.preventDefault()
      onCloseRedeem()
      return true
    }

    if (discountOpen && !mutating) {
      event.preventDefault()
      onCloseDiscount()
      return true
    }

    if (noteOpen && !mutating) {
      event.preventDefault()
      onCloseNote()
      return true
    }

    if (customerHistoryOpen && !mutating) {
      event.preventDefault()
      onCloseCustomerHistory()
      return true
    }

    if (selectedLineId && !mutating && onClearLineSelection) {
      event.preventDefault()
      onClearLineSelection()
      return true
    }

    return false
  }

  if (event.key === 'F8') {
    event.preventDefault()
    onOpenDiscount()
    return true
  }

  if (event.key === 'F9') {
    event.preventDefault()
    onOpenVoucher()
    return true
  }

  if (event.key === 'F10') {
    event.preventDefault()
    onOpenSuspended()
    return true
  }

  if (event.key === 'F2') {
    event.preventDefault()
    onFocusCustomer()
    return true
  }

  if (event.key === 'F3') {
    event.preventDefault()
    onAppointmentShortcut()
    return true
  }

  if (event.key === 'F6') {
    event.preventDefault()
    onPackageShortcut()
    return true
  }

  if (event.key === 'F7') {
    event.preventDefault()
    onRedeemShortcut()
    return true
  }

  if (event.key === 'F4') {
    event.preventDefault()
    onOpenPayment()
    return true
  }

  if (event.key === 'F5') {
    event.preventDefault()
    if (canHoldSale(sale)) {
      onHoldSale()
    } else {
      onOpenSuspended()
    }
    return true
  }

  if (isPlusKey(event.key) || isMinusKey(event.key)) {
    const parentLines = getParentSaleLines(sale)
    const lineId = resolveActiveLineId(parentLines, selectedLineId)
    const line = parentLines.find((item) => item.id === lineId)

    if (!line || mutating) {
      return false
    }

    const delta = isPlusKey(event.key) ? 1 : -1
    const nextQuantity = adjustLineQuantity(line.quantity, delta)

    if (nextQuantity === line.quantity) {
      return false
    }

    event.preventDefault()
    onChangeLineQuantity(lineId, nextQuantity)
    return true
  }

  return false
}
